'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { X, Share2, ShieldCheck, Sparkles, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase-browser';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Link from 'next/link';

export default function SmartPrompt() {
  const pathname = usePathname();
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [linkedinVerified, setLinkedinVerified] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  const supabase = createClient();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Fetch authentication state and LinkedIn verification status
  useEffect(() => {
    if (!isMounted) return;

    async function checkAuthState() {
      try {
        let { data: { user: currentUser } } = await supabase.auth.getUser();

        if (currentUser) {
          setUser(currentUser);
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('linkedin_verified')
            .eq('user_id', currentUser.id)
            .maybeSingle();

          if (profile) {
            setLinkedinVerified(!!profile.linkedin_verified);
          }
        }
      } catch (e) {
        console.error('SmartPrompt auth check error:', e);
      } finally {
        setLoading(false);
      }
    }

    checkAuthState();
  }, [isMounted, supabase]);

  // Handle slide-in (5s delay) and auto-dismiss (9s visibility)
  useEffect(() => {
    if (!isMounted || loading) return;

    // Check if dismissed in this browser session
    const isDismissed = sessionStorage.getItem('smart-prompt-dismissed') === 'true';
    if (isDismissed) return;

    // Render nothing if user is logged in AND already verified
    if (user && linkedinVerified) return;

    // Slide-in after 5 seconds
    const showTimeout = setTimeout(() => {
      setIsVisible(true);
    }, 5000);

    return () => clearTimeout(showTimeout);
  }, [isMounted, loading, user, linkedinVerified]);

  // Auto-hide after 9 seconds of visibility, paused on hover
  useEffect(() => {
    if (isVisible && !isHovered) {
      const hideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, 9000);

      return () => clearTimeout(hideTimeout);
    }
  }, [isVisible, isHovered]);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('smart-prompt-dismissed', 'true');
  };

  const handleLinkedInConnect = async () => {
    setIsLinking(true);
    try {
      const { error } = await supabase.auth.linkIdentity({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(pathname)}`,
        },
      });
      if (error) throw error;
    } catch (e: any) {
      console.error('SmartPrompt LinkedIn Link Error:', e);
      toast.error(e.message || 'Failed to connect LinkedIn. Please try again.');
      setIsLinking(false);
    }
  };

  if (!isMounted || loading) return null;
  if (user && linkedinVerified) return null;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`fixed bottom-6 right-6 z-50 w-[350px] max-w-[calc(100vw-3rem)] rounded-2xl border border-border/80 bg-card/95 backdrop-blur-md shadow-2xl p-5 transition-all duration-500 ease-in-out transform ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[120%] opacity-0 pointer-events-none'
      }`}
      aria-live="polite"
    >
      {/* Close button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3.5 right-3.5 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all cursor-pointer"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Content A: NOT logged in */}
      {!user && (
        <div className="space-y-4">
          <div className="space-y-1 pr-6">
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-primary flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Community Membership
            </span>
            <h4 className="text-sm font-bold text-foreground tracking-tight">Join GuideToReviews</h4>
            <p className="text-xs text-muted-foreground leading-normal">
              Save your favorite tools, write peer reviews, and get personalized AI assistant suggestions.
            </p>
          </div>
          <div className="flex items-center gap-2.5 pt-1">
            <Link href="/register" className="flex-1" onClick={handleDismiss}>
              <Button size="sm" className="w-full text-xs font-bold rounded-xl h-9 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md shadow-primary/10">
                Register
              </Button>
            </Link>
            <Link href="/login" className="flex-1" onClick={handleDismiss}>
              <Button size="sm" variant="outline" className="w-full text-xs font-bold rounded-xl h-9 border-border/60 hover:bg-muted/40">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Content B: Logged in AND linkedin_verified = false */}
      {user && !linkedinVerified && (
        <div className="space-y-4">
          <div className="space-y-1 pr-6">
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#0077b5] flex items-center gap-1">
              <Share2 className="h-3 w-3" />
              Profile Verification
            </span>
            <h4 className="text-sm font-bold text-foreground tracking-tight">Link LinkedIn Account</h4>
            <p className="text-xs text-muted-foreground leading-normal">
              Add a soft trust signal to your community reviews. Linking is optional and simply confirms you control a real account.
            </p>
          </div>
          <div className="space-y-2 pt-1">
            <Button
              size="sm"
              onClick={handleLinkedInConnect}
              disabled={isLinking}
              className="w-full text-xs font-bold rounded-xl h-9 bg-[#0077b5] hover:bg-[#006297] text-white gap-1.5 shadow-md shadow-primary/5 cursor-pointer"
            >
              {isLinking ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Share2 className="h-3.5 w-3.5" />
              )}
              {isLinking ? 'Redirecting...' : 'Connect LinkedIn'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="w-full text-[10px] font-semibold text-muted-foreground hover:text-foreground h-7"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
